import { Component, TemplateFunction } from '@ribajs/core'
import { CustomerFavoritesService } from '../services/index.js'

import type { ShopifyProduct, ShopifyProductVariant } from '../types/index.js'

interface Scope {
  // OPTIONS
  product?: ShopifyProduct
  variant?: ShopifyProductVariant
  /** Current (main or sub) collection */
  collectionHandle?: string
  showRemoveButton: boolean
  /** If true a add-to-cart loading animation is shown */
  pending: boolean
  /** If true a add-to-favorites process was success */
  success: boolean
  /** If true a add-to-favorites process was failed */
  failed: boolean
  /** If true a this product was already added to favorites */
  isAdded: boolean

  // METHODS
  add: SbAddToFavoritesComponent['add']
  remove: SbAddToFavoritesComponent['remove']
  toggle: SbAddToFavoritesComponent['toggle']

  // TEMPLATES
}

export class SbAddToFavoritesComponent extends Component {
  public static tagName = 'sb-add-to-favorites'

  lifecycleOptions = {
    ignore: true,
  }

  public scope: Scope = {
    // OPTIONS
    product: undefined,
    variant: undefined,
    collectionHandle: undefined,
    showRemoveButton: false,
    pending: false,
    success: false,
    failed: false,

    isAdded: false,

    // METHODS
    add: this.add,
    remove: this.remove,
    toggle: this.toggle,

    // TEMPLATES
  }

  static get observedAttributes(): string[] {
    return ['product', 'variant', 'collection-handle', 'show-remove-button']
  }

  protected requiredAttributes(): string[] {
    return ['product']
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbAddToFavoritesComponent.observedAttributes)
    this.refresh()
    this.addEventListeners()
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  /**
   * Add the product (and the selected variant (selected by the selected options)) to the favorites
   * @returns True if the product was added to the favorites
   */
  public async add(event?: Event) {
    if (!this.scope.product) {
      return
    }
    event?.preventDefault()
    await CustomerFavoritesService.addProduct(
      this.scope.product.handle,
      this.scope.variant?.id,
    )
    this.scope.isAdded = await CustomerFavoritesService.productIsAdded(
      this.scope.product.handle,
    )
    return this.scope.isAdded
  }

  /**
   * Removes the product from the favorites
   * @param event
   * @returns False if the product was removed from the favorites
   */
  public async remove(event?: Event) {
    if (!this.scope.product) {
      return
    }
    event?.preventDefault()
    await CustomerFavoritesService.removeProduct(this.scope.product.handle)
    this.scope.isAdded = await CustomerFavoritesService.productIsAdded(
      this.scope.product.handle,
    )
    return this.scope.isAdded
  }

  /**
   * Adds or removes the product from the favorites
   * @returns True if the product was added to the favorites and false if the product was removed from the favorites
   */
  public async toggle() {
    if (!this.scope.product) {
      return
    }
    if (this.scope.isAdded) {
      await this.remove()
    } else {
      await this.add()
    }
    return this.scope.isAdded
  }

  protected async refresh() {
    if (this.scope.product) {
      this.scope.isAdded = await CustomerFavoritesService.productIsAdded(
        this.scope.product.handle,
      )
    }
  }

  async afterBind() {
    await super.afterBind()
    this.refresh()
    this.classList.add(`${SbAddToFavoritesComponent.tagName}-ready`)
  }

  protected addEventListeners() {
    // Noting yet
  }

  protected removeEventListeners() {
    // Noting yet
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
